import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  ArrowLeft,
  Pencil,
  Trash2,
  Plus,
  Loader2,
  ClipboardList,
  Package,

  Copy,
  Check,
} from 'lucide-react'
import {
  useTestPlan,
  useUpdateTestPlan,
  useDeleteTestPlan,
  useTestPlanTestCases,
  useAssignTestCase,
  useRemoveTestCase,
} from '../api/useTestPlans'
import { useBuilds } from '../api/useBuilds'
import { usePlatforms } from '../api/usePlatforms'
import { useTestCases } from '@/features/test-cases/api/useTestCases'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { toast } from 'sonner'
import { TestUrgency } from '@/types/testplan.types'

const formatDate = (dateString: string): string =>
  new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

const getUrgencyLabel = (urgency: number): string => {
  switch (urgency) {
    case TestUrgency.Low: return 'Low'
    case TestUrgency.Medium: return 'Medium'
    case TestUrgency.High: return 'High'
    default: return `${urgency}`
  }
}

const getUrgencyColor = (urgency: number): string => {
  switch (urgency) {
    case TestUrgency.High: return 'bg-red-100 text-red-700'
    case TestUrgency.Medium: return 'bg-yellow-100 text-yellow-700'
    case TestUrgency.Low: return 'bg-green-100 text-green-700'
    default: return 'bg-gray-100 text-gray-700'
  }
}

export default function TestPlanDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const testplanId = parseInt(id || '0', 10)

  // Data hooks
  const { data: testplan, isLoading } = useTestPlan(testplanId)
  const { data: assignments } = useTestPlanTestCases(testplanId)
  const { data: builds } = useBuilds({ testplan_id: testplanId })
  const { data: allTestCases } = useTestCases()
  const { data: platforms } = usePlatforms()

  // Mutations
  const updateMutation = useUpdateTestPlan()
  const deleteMutation = useDeleteTestPlan()
  const assignMutation = useAssignTestCase()
  const removeMutation = useRemoveTestCase()

  // UI state
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [assignOpen, setAssignOpen] = useState(false)
  const [removeTarget, setRemoveTarget] = useState<{ id: number; name: string } | null>(null)
  const [copiedKey, setCopiedKey] = useState(false)

  // Edit form state
  const [editName, setEditName] = useState('')
  const [editNotes, setEditNotes] = useState('')
  const [editActive, setEditActive] = useState('true')
  const [editOpen_, setEditOpen_] = useState('true')
  const [editPublic, setEditPublic] = useState('false')

  // Assign form state
  const [assignTestCaseId, setAssignTestCaseId] = useState<string>('')
  const [assignPlatformId, setAssignPlatformId] = useState<string>('any')
  const [assignUrgency, setAssignUrgency] = useState<string>(TestUrgency.Medium.toString())

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!testplan) {
    return (
      <div className="text-center py-20">
        <h2 className="text-2xl font-bold">Test Plan Not Found</h2>
        <p className="text-muted-foreground mt-2">The requested test plan does not exist.</p>
        <Button className="mt-4" onClick={() => navigate('/tests/test-plans')}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Test Plans
        </Button>
      </div>
    )
  }

  // Filter out already-assigned test cases for the assign dialog
  const assignedTcIds = new Set((assignments || []).map((a) => a.tcversion_id))
  const availableTestCases = (allTestCases || []).filter(
    (tc) => !assignedTcIds.has(tc.id)
  )

  const handleEditOpen = () => {
    setEditName(testplan.name)
    setEditNotes(testplan.notes || '')
    setEditActive(testplan.active.toString())
    setEditOpen_(testplan.is_open.toString())
    setEditPublic(testplan.is_public.toString())
    setEditOpen(true)
  }

  const handleEditSubmit = async () => {
    try {
      await updateMutation.mutateAsync({
        id: testplanId,
        data: {
          name: editName.trim(),
          notes: editNotes.trim() || undefined,
          active: editActive === 'true',
          is_open: editOpen_ === 'true',
          is_public: editPublic === 'true',
        },
      })
      toast.success('Test plan updated')
      setEditOpen(false)
    } catch (err: unknown) {
      let msg = 'Failed to update test plan'
      if (err && typeof err === 'object' && 'response' in err) {
        const e = err as { response?: { data?: { detail?: string } } }
        if (e.response?.data?.detail) msg = e.response.data.detail
      }
      toast.error(msg)
    }
  }

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(testplanId)
      toast.success('Test plan deleted')
      navigate('/tests/test-plans')
    } catch {
      toast.error('Failed to delete test plan')
    }
  }

  const handleAssign = async () => {
    if (!assignTestCaseId) return

    try {
      await assignMutation.mutateAsync({
        testplanId,
        data: {
          tcversion_id: parseInt(assignTestCaseId, 10),
          platform_id: assignPlatformId && assignPlatformId !== 'any' ? parseInt(assignPlatformId, 10) : undefined,
          urgency: parseInt(assignUrgency, 10),
        },
      })
      toast.success('Test case assigned')
      setAssignOpen(false)
      setAssignTestCaseId('')
      setAssignPlatformId('any')
      setAssignUrgency(TestUrgency.Medium.toString())
    } catch (err: unknown) {
      let msg = 'Failed to assign test case'
      if (err && typeof err === 'object' && 'response' in err) {
        const e = err as { response?: { data?: { detail?: string } } }
        if (e.response?.data?.detail) msg = e.response.data.detail
      }
      toast.error(msg)
    }
  }

  const handleRemove = async () => {
    if (!removeTarget) return
    try {
      await removeMutation.mutateAsync({
        testplanId,
        assignmentId: removeTarget.id,
      })
      toast.success(`"${removeTarget.name}" removed from plan`)
    } catch {
      toast.error('Failed to remove test case')
    } finally {
      setRemoveTarget(null)
    }
  }

  const handleCopyApiKey = () => {
    if (testplan.api_key) {
      navigator.clipboard.writeText(testplan.api_key)
      setCopiedKey(true)
      toast.success('API key copied')
      setTimeout(() => setCopiedKey(false), 2000)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={() => navigate('/tests/test-plans')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{testplan.name}</h1>
            <div className="flex items-center gap-2 mt-1">
              {testplan.project_prefix && (
                <Badge variant="outline">{testplan.project_prefix}</Badge>
              )}
              <Badge variant={testplan.active ? 'default' : 'secondary'}>
                {testplan.active ? 'Active' : 'Inactive'}
              </Badge>
              <Badge variant={testplan.is_open ? 'default' : 'secondary'}>
                {testplan.is_open ? 'Open' : 'Closed'}
              </Badge>
              {testplan.is_public && <Badge variant="outline">Public</Badge>}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleEditOpen}>
            <Pencil className="mr-2 h-4 w-4" /> Edit
          </Button>
          <Button variant="outline" onClick={() => setDeleteOpen(true)}>
            <Trash2 className="mr-2 h-4 w-4 text-red-500" /> Delete
          </Button>
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Test Cases</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{assignments?.length || testplan.testcase_count || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Builds</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{builds?.length || testplan.build_count || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Created</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm font-medium">{formatDate(testplan.created_at)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">API Key</CardTitle>
          </CardHeader>
          <CardContent>
            {testplan.api_key ? (
              <Button variant="outline" size="sm" onClick={handleCopyApiKey}>
                {copiedKey ? <Check className="mr-1 h-3 w-3" /> : <Copy className="mr-1 h-3 w-3" />}
                {copiedKey ? 'Copied' : 'Copy Key'}
              </Button>
            ) : (
              <span className="text-sm text-muted-foreground">{'\u2014'}</span>
            )}
          </CardContent>
        </Card>
      </div>

      {testplan.notes && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">{testplan.notes}</p>
          </CardContent>
        </Card>
      )}

      {/* Tabs: Test Cases & Builds */}
      <Tabs defaultValue="testcases">
        <TabsList>
          <TabsTrigger value="testcases">
            <ClipboardList className="mr-2 h-4 w-4" />
            Assigned Test Cases ({assignments?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="builds">
            <Package className="mr-2 h-4 w-4" />
            Builds ({builds?.length || 0})
          </TabsTrigger>
        </TabsList>

        {/* Assigned Test Cases Tab */}
        <TabsContent value="testcases" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Assigned Test Cases</CardTitle>
                <Button size="sm" onClick={() => setAssignOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" /> Assign Test Case
                </Button>
              </div>
              <CardDescription>
                Test cases assigned to this plan with platform and urgency settings.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {assignments && assignments.length > 0 ? (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Test Case</TableHead>
                        <TableHead>Platform</TableHead>
                        <TableHead>Urgency</TableHead>
                        <TableHead>Order</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {assignments.map((a) => (
                        <TableRow key={a.id}>
                          <TableCell className="font-medium">{a.testcase_name}</TableCell>
                          <TableCell className="text-muted-foreground">
                            {a.platform_name || '\u2014'}
                          </TableCell>
                          <TableCell>
                            <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${getUrgencyColor(a.urgency)}`}>
                              {getUrgencyLabel(a.urgency)}
                            </span>
                          </TableCell>
                          <TableCell className="text-muted-foreground">{a.node_order}</TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setRemoveTarget({ id: a.id, name: a.testcase_name })}
                              disabled={removeMutation.isPending}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <ClipboardList className="mx-auto h-10 w-10 text-muted-foreground/50" />
                  <p className="mt-2 text-muted-foreground">No test cases assigned yet.</p>
                  <Button size="sm" className="mt-3" onClick={() => setAssignOpen(true)}>
                    <Plus className="mr-2 h-4 w-4" /> Assign Test Case
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Builds Tab */}
        <TabsContent value="builds" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Builds</CardTitle>
                <Button size="sm" asChild>
                  <Link to="/tests/builds">
                    <Package className="mr-2 h-4 w-4" /> Manage Builds
                  </Link>
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {builds && builds.length > 0 ? (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Build Name</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Release Date</TableHead>
                        <TableHead>Created</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {builds.map((b) => (
                        <TableRow key={b.id}>
                          <TableCell className="font-medium">{b.name}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Badge variant={b.active ? 'default' : 'secondary'}>
                                {b.active ? 'Active' : 'Inactive'}
                              </Badge>
                              <Badge variant={b.is_open ? 'outline' : 'secondary'}>
                                {b.is_open ? 'Open' : 'Closed'}
                              </Badge>
                            </div>
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {b.release_date || '\u2014'}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {formatDate(b.created_at)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Package className="mx-auto h-10 w-10 text-muted-foreground/50" />
                  <p className="mt-2 text-muted-foreground">No builds for this plan yet.</p>
                  <Button size="sm" className="mt-3" asChild>
                    <Link to="/tests/builds">
                      <Plus className="mr-2 h-4 w-4" /> Create Build
                    </Link>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ===== DIALOGS ===== */}

      {/* Edit Test Plan Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Test Plan</DialogTitle>
            <DialogDescription>Update test plan settings.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-name">Name</Label>
              <Input id="edit-name" value={editName} onChange={(e) => setEditName(e.target.value)} />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-notes">Notes</Label>
              <Textarea id="edit-notes" value={editNotes} onChange={(e) => setEditNotes(e.target.value)} rows={3} />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="grid gap-2">
                <Label>Active</Label>
                <Select value={editActive} onValueChange={setEditActive}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Yes</SelectItem>
                    <SelectItem value="false">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Open</Label>
                <Select value={editOpen_} onValueChange={setEditOpen_}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Yes</SelectItem>
                    <SelectItem value="false">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Public</Label>
                <Select value={editPublic} onValueChange={setEditPublic}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Yes</SelectItem>
                    <SelectItem value="false">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={handleEditSubmit} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Test Case Dialog */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Assign Test Case</DialogTitle>
            <DialogDescription>
              Add a test case to this plan with platform and urgency settings.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Test Case <span className="text-red-500">*</span></Label>
              <Select value={assignTestCaseId} onValueChange={setAssignTestCaseId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a test case" />
                </SelectTrigger>
                <SelectContent>
                  {availableTestCases.length === 0 ? (
                    <SelectItem value="none" disabled>
                      No unassigned test cases available
                    </SelectItem>
                  ) : (
                    availableTestCases.map((tc) => (
                      <SelectItem key={tc.id} value={tc.id.toString()}>
                        {tc.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Platform (optional)</Label>
              <Select value={assignPlatformId} onValueChange={setAssignPlatformId}>
                <SelectTrigger>
                  <SelectValue placeholder="Any platform" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any platform</SelectItem>
                  {platforms?.map((p) => (
                    <SelectItem key={p.id} value={p.id.toString()}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Urgency</Label>
              <Select value={assignUrgency} onValueChange={setAssignUrgency}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={TestUrgency.Low.toString()}>Low</SelectItem>
                  <SelectItem value={TestUrgency.Medium.toString()}>Medium</SelectItem>
                  <SelectItem value={TestUrgency.High.toString()}>High</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignOpen(false)}>Cancel</Button>
            <Button
              onClick={handleAssign}
              disabled={!assignTestCaseId || assignMutation.isPending}
            >
              {assignMutation.isPending ? 'Assigning...' : 'Assign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Test Plan</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &ldquo;{testplan.name}&rdquo;?
              This will remove all test case assignments. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete Plan'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Remove Assignment Confirmation */}
      <AlertDialog open={!!removeTarget} onOpenChange={() => setRemoveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Test Case</AlertDialogTitle>
            <AlertDialogDescription>
              Remove &ldquo;{removeTarget?.name}&rdquo; from this test plan?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemove}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
